import React, { useState, useEffect } from 'react';
import { analyticsApi, SystemMetrics } from '../services/api';

interface DashboardData {
  system_status: {
    overall: string;
    services_online: number;
    total_services: number;
    cpu_usage: number;
    memory_usage: number;
    redis_connected: boolean;
  };
  business_summary: {
    total_tenants: number;
    total_documents: number;
    documents_today: number;
    success_rate: number;
    avg_processing_time: string;
  };
  recent_activity: {
    total_events: number;
    error_events: number;
    latest_events: any[];
  };
  alerts: any[];
}

const DashboardOverview: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Try to fetch from analytics service, use mock data if unavailable
      try {
        const [overviewResponse, metricsResponse] = await Promise.all([
          analyticsApi.getDashboardOverview(),
          analyticsApi.getSystemMetrics(),
        ]);

        setDashboardData(overviewResponse.data);
        setSystemMetrics(metricsResponse.data);
      } catch (apiErr: any) {
        // Analytics service unavailable - use mock data
        console.warn('Analytics service unavailable, using mock data:', apiErr.message);

        // Mock dashboard data
        setDashboardData({
          system_status: {
            overall: 'healthy',
            services_online: 4,
            total_services: 7,
            cpu_usage: 0,
            memory_usage: 0,
            redis_connected: true,
          },
          business_summary: {
            total_tenants: 0,
            total_documents: 0,
            documents_today: 0,
            success_rate: 0,
            avg_processing_time: 'N/A',
          },
          recent_activity: {
            total_events: 0,
            error_events: 0,
            latest_events: [],
          },
          alerts: [],
        });

        // Mock system metrics
        setSystemMetrics({
          cpu_percent: 0,
          memory_percent: 0,
          disk_percent: 0,
          redis_connected: true,
          timestamp: new Date().toISOString(),
          services_status: {},
        });
      }

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="text-red-400">⚠️</div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Dashboard</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getUsageColor = (percentage: number) => {
    if (percentage > 80) return 'bg-red-500';
    if (percentage > 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-900">Dashboard Overview</h2>
        <div className="flex items-center text-sm text-gray-500">
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
          {loading && <div className="ml-2 animate-spin h-4 w-4 border-2 border-blue-600 rounded-full border-t-transparent"></div>}
        </div>
      </div>

      {/* System Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Overall Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(dashboardData?.system_status.overall || 'unknown')}`}>
              {dashboardData?.system_status.overall || 'Unknown'}
            </div>
          </div>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {dashboardData?.system_status.services_online || 0}/{dashboardData?.system_status.total_services || 0}
          </p>
          <p className="text-sm text-gray-600">Services Online</p>
        </div>

        {/* CPU Usage */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">CPU Usage</p>
            <span className="text-2xl">💻</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {systemMetrics?.cpu_percent.toFixed(1) || '0.0'}%
          </p>
          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getUsageColor(systemMetrics?.cpu_percent || 0)}`}
              style={{ width: `${Math.min(systemMetrics?.cpu_percent || 0, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Memory Usage */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Memory Usage</p>
            <span className="text-2xl">🧠</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {systemMetrics?.memory_percent.toFixed(1) || '0.0'}%
          </p>
          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getUsageColor(systemMetrics?.memory_percent || 0)}`}
              style={{ width: `${Math.min(systemMetrics?.memory_percent || 0, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Redis Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Redis Cache</p>
            <span className="text-2xl">🗄️</span>
          </div>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {systemMetrics?.redis_connected ? '✓' : '✗'}
          </p>
          <p className={`text-sm ${systemMetrics?.redis_connected ? 'text-green-600' : 'text-red-600'}`}>
            {systemMetrics?.redis_connected ? 'Connected' : 'Disconnected'}
          </p>
        </div>
      </div>

      {/* Business Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Total Tenants</p>
            <span className="text-xl">🏢</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-blue-600">
            {dashboardData?.business_summary.total_tenants || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Total Documents</p>
            <span className="text-xl">📄</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-blue-600">
            {dashboardData?.business_summary.total_documents || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Today's Documents</p>
            <span className="text-xl">📊</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-green-600">
            {dashboardData?.business_summary.documents_today || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Success Rate</p>
            <span className="text-xl">✅</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-green-600">
            {dashboardData?.business_summary.success_rate || 0}%
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Avg Processing</p>
            <span className="text-xl">⚡</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-purple-600">
            {dashboardData?.business_summary.avg_processing_time || 'N/A'}
          </p>
        </div>
      </div>

      {/* Recent Activity & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Events (Last Hour)</span>
                <span className="font-medium">{dashboardData?.recent_activity.total_events || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Error Events</span>
                <span className={`font-medium ${(dashboardData?.recent_activity.error_events || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {dashboardData?.recent_activity.error_events || 0}
                </span>
              </div>
            </div>

            {dashboardData?.recent_activity.latest_events && dashboardData.recent_activity.latest_events.length > 0 ? (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">Latest Events:</p>
                {dashboardData.recent_activity.latest_events.slice(0, 3).map((event, index) => (
                  <div key={index} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    <span className="font-medium">{event.service || 'Unknown'}</span>: {event.message || 'No message'}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-sm text-gray-500">No recent events</p>
            )}
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">System Alerts</h3>
          </div>
          <div className="p-6">
            {dashboardData?.alerts && dashboardData.alerts.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.alerts.map((alert, index) => (
                  <div key={index} className={`p-3 rounded border-l-4 ${
                    alert.type === 'critical' ? 'bg-red-50 border-red-400' :
                    alert.type === 'warning' ? 'bg-yellow-50 border-yellow-400' :
                    'bg-blue-50 border-blue-400'
                  }`}>
                    <p className="text-sm font-medium text-gray-900">{alert.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <span className="text-4xl">✅</span>
                <p className="mt-2 text-sm text-gray-500">No active alerts</p>
                <p className="text-xs text-gray-400">All systems are running normally</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;